import type { Room } from 'livekit-client';
import { Track } from 'livekit-client';
import {
  capLocalCameraPublishBitrate,
  restoreLocalCameraPublishBitrate,
} from '@/lib/camera-publish-bitrate';
import { readEconomyModeEnabled } from '@/lib/economyMode';
import {
  getEffectiveVideoQualityProfile,
  getVideoCaptureMediaConstraints,
  readVideoQualityProfile,
} from '@/lib/video-quality-profile';

/**
 * Soft-start SEM reduzir resolução de captura.
 *
 * Antes: applyConstraints 640×360 → o LiveKit publicava só 2 camadas (sem HIGH
 * 540p). O host ficava “preso” em 360p e TODOS os outros viam o host ruim —
 * parecia que o PC dele “baixou a qualidade da sala”.
 *
 * Agora: publica no perfil normal (540p + 3 camadas) e só limita o bitrate
 * por alguns segundos; depois restaura o teto do perfil.
 */
export const MEDIA_SOFT_START_MS = 3_000;

/** Pequena pausa após Connected antes de publicar vídeo. */
export const MEDIA_POST_CONNECT_SETTLE_MS = 400;

/** Bitrate temporário no aquecimento (camada alta ainda existe, só mais leve). */
export const SOFT_START_CAMERA_MAX_BITRATE = 400_000;

export function getLocalCameraMediaStreamTrack(
  room: Room,
): MediaStreamTrack | undefined {
  const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
  const t = pub?.track as { mediaStreamTrack?: MediaStreamTrack } | undefined;
  const ms = t?.mediaStreamTrack;
  return ms && ms.readyState === 'live' ? ms : undefined;
}

/** @deprecated — não usar para soft-start (encolhia o publish e matava a camada HIGH). */
export function getSoftStartCameraConstraints(): MediaTrackConstraints {
  return {
    width: { ideal: 640, max: 640 },
    height: { ideal: 360, max: 360 },
    frameRate: { max: 15 },
    aspectRatio: { ideal: 16 / 9 },
  };
}

export async function applySoftStartCameraConstraints(room: Room): Promise<void> {
  // Mantido por compat de testes/imports — soft-start real usa bitrate only.
  await capLocalCameraPublishBitrate(room, {
    maxBitrate: SOFT_START_CAMERA_MAX_BITRATE,
    maxFramerate: 20,
  });
}

export async function restoreCameraConstraintsAfterSoftStart(room: Room): Promise<void> {
  const msTrack = getLocalCameraMediaStreamTrack(room);
  if (msTrack) {
    try {
      const economy = readEconomyModeEnabled();
      const effective = getEffectiveVideoQualityProfile(readVideoQualityProfile(), economy);
      await msTrack.applyConstraints(getVideoCaptureMediaConstraints(effective));
    } catch {
      /* ignore */
    }
  }
  await restoreLocalCameraPublishBitrate(room);
}

/**
 * Soft-start por bitrate (não por resolução). Agenda restore do perfil.
 */
export function beginCameraSoftStart(
  room: Room,
  softStartMs: number = MEDIA_SOFT_START_MS,
): { promise: Promise<void>; cancel: () => void } {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const promise = (async () => {
    // Garante captura no perfil (caso algo tenha baixado antes).
    const msTrack = getLocalCameraMediaStreamTrack(room);
    if (msTrack) {
      try {
        const economy = readEconomyModeEnabled();
        const effective = getEffectiveVideoQualityProfile(readVideoQualityProfile(), economy);
        await msTrack.applyConstraints(getVideoCaptureMediaConstraints(effective));
      } catch {
        /* ignore */
      }
    }
    await capLocalCameraPublishBitrate(room, {
      maxBitrate: SOFT_START_CAMERA_MAX_BITRATE,
      maxFramerate: 20,
    });
    if (cancelled) return;
    await new Promise<void>((resolve) => {
      timer = setTimeout(resolve, softStartMs);
    });
    if (cancelled) return;
    await restoreCameraConstraintsAfterSoftStart(room);
  })();

  return {
    promise,
    cancel: () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    },
  };
}
